import { FC, useEffect, useState } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import { ViewDto } from "@ryde/shared";

import { dashboardDefaultViewAppliedAtom } from "../../dynamic-dashboard.atoms";
import { useDashboardViews } from "../../hooks/use-dashboard-views";
import { DashboardSaveViewButton } from "./dashboard-save-view-button";
import { DashboardViewDropdown } from "./dashboard-view-dropdown";
import { DashboardDialogViewEdit } from "./dashboard-view-edit";
import { DashboardViewContext } from "./dashboard-view.context";

/**
 * Dashboard fork of the Rides `ViewHandlingPanel`: same Create-View button +
 * Views dropdown + edit dialog composition, wired to the dashboard's card-layout
 * view handlers. On cold load, applies the default view once it resolves.
 */
export const DashboardViewHandlingPanel: FC = () => {
  const [editViewState, setEditViewState] = useState<{
    isOpened: boolean;
    viewDto: ViewDto | undefined;
  }>({ isOpened: false, viewDto: undefined });

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // Reads the loaded atom without fetching. The page content owns the initial views fetch, because
  // its own data request waits for the layout and this panel is not rendered on mobile — `PageHeader`
  // passes `customRightComponent` to the desktop header only.
  const { defaultView, applyView } = useDashboardViews();
  const [defaultApplied, setDefaultApplied] = useRecoilState(
    dashboardDefaultViewAppliedAtom,
  );

  const closeEditView = useEvent(() => {
    setEditViewState({ isOpened: false, viewDto: undefined });
  });

  // On cold load, if a saved view is marked default, apply it ONCE; otherwise
  // the base Default layout (today's dashboard) stays. The "applied" flag is a
  // session atom (not a ref), so navigating away and back does NOT re-apply the
  // default and clobber the user's active view / unsaved edits — it re-applies
  // only after a real page reload.
  useEffect(() => {
    if (defaultApplied || !defaultView) {
      return;
    }
    setDefaultApplied(true);
    applyView(Number(defaultView.id));
  }, [defaultApplied, setDefaultApplied, defaultView, applyView]);

  return (
    <DashboardViewContext.Provider
      value={{
        isDropdownOpen,
        setIsDropdownOpen,
        isEditViewOpen: editViewState.isOpened,
        openEditView: (viewDto?: ViewDto) =>
          setEditViewState({ isOpened: true, viewDto }),
        editView: editViewState.viewDto,
      }}
    >
      <DashboardSaveViewButton />
      <DashboardViewDropdown />
      {editViewState.isOpened && (
        <DashboardDialogViewEdit
          view={editViewState.viewDto}
          onClose={closeEditView}
        />
      )}
    </DashboardViewContext.Provider>
  );
};
